import classNames from 'classnames'
import { memo, useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useTranslate } from '../../../../../hooks/language/use-translate'
import { useGlobalStore } from '../../../../../store/global-store'

const ExperienceToEarn = memo(() => {
  const { isLuck, giftSelectedValue, quantity, usersToGiftSize } =
    useGlobalStore(
      useShallow((state) => ({
        isLuck: state?.selectedType?.isLuck,
        quantity: state.quantity,
        giftSelectedValue: state?.giftSelected?.value,
        usersToGiftSize: state.usersToGift?.size ?? 0,
      })),
    )

  if (giftSelectedValue) {
    const divider = isLuck ? 10 : 1
    // XP de espectador escala com o nº de destinatários (paga por cada um),
    // igual ao backend: userReceivedIds.length * quantityCombo * value.
    const recipients = usersToGiftSize || 1

    return (
      <span className="text-xs font-bold text-hug-lilac">
        +{(giftSelectedValue * quantity * recipients) / divider} XP
      </span>
    )
  }
})

export function TotalValue() {
  const { giftSelected, quantity, usersToGift } = useGlobalStore(
    useShallow((state) => ({
      giftSelected: state.giftSelected,
      quantity: state.quantity,
      usersToGift: state.usersToGift,
    })),
  )

  const { translate } = useTranslate()

  const changeQuantity = useCallback((quantity: number) => {
    useGlobalStore.setState({ quantity })
  }, [])

  const combos: number[] = useMemo(() => {
    if (giftSelected?.allowedCombos) {
      return JSON.parse(giftSelected?.allowedCombos)
    }
  }, [giftSelected])

  const disabled = useMemo(
    () => !quantity || !usersToGift?.size,
    [quantity, usersToGift?.size],
  )

  const handleOpenModalCoinDrop = useCallback(() => {
    if ('ReactNativeWebView' in window) {
      // @ts-ignore
      window.ReactNativeWebView.postMessage('open-modal-coin-drop')
    }
  }, [])

  function handleClickSend() {
    if ('ReactNativeWebView' in window) {
      const giftSelected = useGlobalStore.getState().giftSelected

      if (giftSelected?.isCoinDrop) {
        return handleOpenModalCoinDrop()
      }

      const usersToGift = useGlobalStore.getState().usersToGift
      const quantity = useGlobalStore.getState().quantity
      const selectedType = useGlobalStore.getState().selectedType

      // @ts-ignore
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          giftSelected,
          usersToGift: [...usersToGift],
          quantity,
          selectedType,
        }),
      )
    }
  }

  return (
    <div className="flex flex-row gap-4 p-4 items-center justify-end border-t border-white/10">
      <div className="flex flex-row items-center gap-1.5 flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden">
        {(combos ?? []).map((combo) => (
          <button
            key={combo}
            type="button"
            onClick={() => changeQuantity(combo)}
            className={classNames(
              [
                'text-xs font-semibold h-7 min-w-8 px-2 rounded-full shrink-0 transition-colors',
              ],
              {
                'bg-hug-purple text-white shadow-[0_2px_8px_rgba(155,48,230,0.4)]':
                  quantity === combo,
                'bg-white/10 text-white/70': quantity !== combo,
              },
            )}
          >
            {combo}
          </button>
        ))}
      </div>

      <div className="flex flex-row items-center gap-2">
        <ExperienceToEarn />

        <button
          type="button"
          disabled={disabled}
          className={classNames('py-2 px-5 rounded-full text-sm font-bold', {
            'bg-linear-to-r from-hug-purple to-hug-lilac text-white shadow-[0_2px_10px_rgba(155,48,230,0.45)]':
              !disabled,
            'bg-white/10 text-white/40': disabled,
          })}
          onClick={handleClickSend}
        >
          {translate('modal-gifts-send')}
        </button>
      </div>
    </div>
  )
}
